const { name, version } = require('../../package.json')
import { startOfWeek, addDays, format } from 'date-fns'

export default function (Vuex) {
  return new Vuex.Store({
    state: {
      packageName: name,
      packageVersion: version,
      appEnv: process.env.APP_ENV,
      appStatus: process?.env?.VUE_APP_STATUS ?? process.env.APP_STATUS,
      appMessage: process.env.VUE_APP_MESSAGE,
      showAdminTabs: process.env.VUE_APP_SHOW_ADMIN_TABS,
      showCostExplTab: process.env.VUE_APP_SHOW_COSTEXPL_TAB,
      showAd: process.env.VUE_APP_SHOW_AD,
      user: {
        authenticated: false,
        hasAdminPrivileges: false,
        hasSuperAdminPrivileges: false,
        hasManagerPrivileges: false,
        hasPartnerPrivileges: false,
        details: {}
      },
      userNotifications: {
        total: 0,
        list: []
      },
      // Connection/session state surfaced by StatusBar.vue. Null when healthy.
      // Distinct from `appStatus` above, which is the env-driven maintenance
      // flag and has nothing to do with the live connection.
      statusBanner: null,
      components: {
        staffing: {
          userRoles: [],
          selectedRoles: ['manager', 'associate', 'senior_analyst', 'analyst', 'ftintern'],
          roleLabels: {
            'manager': 'Manager',
            'associate': 'Associate',
            'senior_analyst': 'Senior Analyst',
            'analyst': 'Analyst',
            'ftintern': 'Full-time Intern',
            'admin': 'Admin Team'
          },
          users: [],
          activeProjects: [],
          calendarWeeksStartDate: null,
          projectToDrag: null,
          assigningQueue: [],
          lastQueueUpdate: []
        },
        staffinglogs: {
          userRoles: [],
          selectedRoles: ['manager', 'associate', 'senior_analyst', 'analyst', 'ftintern'],
          roleLabels: {
            'manager': 'Manager',
            'associate': 'Associate',
            'senior_analyst': 'Senior Analyst',
            'analyst': 'Analyst',
            'ftintern': 'Full-time Intern',
            'admin': 'Admin Team'
          },
          users: [],
          calendarWeeksStartDate: null
        },
        timelogs: {
          calendarWeeksStartDate: null,
          monthInfo: format(new Date(), 'MMMM yyyy'),
          calendarFields: [],
          logsInQueue: [],
          logsToRemoveQueue: []
        },
        reports: {
          filtersInfo: {
            projectTitles: [],
            userFullName: '',
          },
          filters: {
            user: null,
            projects: [],
            dates: []
          }
        },
        expenses: {
          statusOptions: [],
          selectedStatus: [],
          selectedDateRange: {},
          searchQuery: '',
          pager: {}
        },
        userexpenses: {
          pager: {}
        },
        leaves: {
          newHoliday: {
            selectedYear: new Date().getFullYear()
          },
          approvedList: [], // the half days are not included!
          filters: {
            user: {
              fullName: null,
              uid: null
            },
            labels: [{
              value: 'submittedDate',
              text: 'Filter by Submitted Date'
            }, {
              value: 'leaveDates',
              text: 'Filter by Leave Dates'
            }],
            dateLabel: 'submittedDate',
            dates: []
          }
        },
        formData: {
          type: 'def',
          project: 'def',
          dates: null,
          expenseProjType: 'prj',
          holidayType: null,
          holidayHalfDayMeridiem: 'AM',
          category: 'def',
          currency: 'gbp',
          amount: 0,
          holiday: 'full',
          duration: '1',
          approvalmanager: 'def',
          vat: {
            value: '0',
            amount: 0
          },
          totalDays: 1,
          notes: '',
          file: null,
          totalBankHolidaysInDates: 0
        },
        formLayouts: {
          FrmHolidayType: false,
          FrmProject: false,
          FrmDate: false,
          FrmDateRange: false,
          FrmCategories: false,
          FrmAmount: false,
          FrmReceipt: false,
          FrmManager: false,
          FrmVAT: false,
          FrmTotalDays: false,
          FrmHours: false,
          FrmNotes: false
        },
        assetBoard: {
          page: 1,
          perPage: 100,
          filters: {},
          sort: [],
          clientView: false,
          selectedAssets: [],
          rejectedAssets: [],
          tab: 'all'
        },
        activityItems: {
          holidayItems: [],
          expenseItems: []
        },
        projectsTracker: {
          page: 1,
          perPage: 25,
          total: 0,
          cacheControl: 'cache'
        },
        userStats: {
          page: 1,
          perPage: 100,
          filters: {},
          sort: []
        },
      },
      caches: {
        bankHolidays: {
          ts: null,
          data: []
        },
      }
    },
    getters: {
      appName: (state) => {
        return state.packageName
      },
      appVersion: (state) => {
        return state.packageVersion
      },
      appEnv: (state) => {
        return state.appEnv
      },
      appStatus: (state) => {
        return state.appStatus
      },
      statusBanner: (state) => {
        return state.statusBanner
      },
      appMessage: (state) => {
        return state.appMessage
      },
      showAdminTabs: (state) => {
        return state.showAdminTabs === 'true'
      },
      showCostExplTab: (state) => {
        return state.showCostExplTab === 'true'
      },
      showAd: (state) => {
        return state.showAd === 'true'
      },
      userAuth: (state) => {
        return state.user
      },
      userNotifications: (state) => {
        return state.userNotifications
      },
      newHolidaySelectedYear: (state) => {
        return state.components.leaves.newHoliday.selectedYear
      },
      staffingActiveProjects: (state) => {
        return state.components.staffing.activeProjects
      },
      staffingDraggedProject: (state) => {
        return state.components.staffing.projectToDrag
      },
      staffingUsers: (state) => {
        return state.components.staffing.users.length ? state.components.staffing.users : []
      },
      staffingLogsUsers: (state) => {
        return state.components.staffinglogs.users.length ? state.components.staffinglogs.users : []
      },
      staffingUserRoles: (state) => {
        return state.components.staffing.userRoles
      },
      staffingUserRoleLabels: (state) => {
        return state.components.staffing.roleLabels
      },
      staffingSelectedUserRoles: (state) => {
        return state.components.staffing.selectedRoles
      },
      staffingCalendarWeeksStartDate: (state) => {
        return state.components.staffing.calendarWeeksStartDate
      },
      staffingLogsCalendarWeeksStartDate: (state) => {
        return state.components.staffinglogs.calendarWeeksStartDate
      },
      assigningQueue(state) {
        return state.components.staffing.assigningQueue
      },
      lastAssigningQueueUpdate(state) {
        return state.components.staffing.lastQueueUpdate.reverse().slice(0, 3)
      },
      calendarWeeksStartDate: (state) => {
        return state.components.timelogs.calendarWeeksStartDate
      },
      timelogsInQueue: (state) => {
        return state.components.timelogs.logsInQueue
      },
      timelogsToRemoveQueue: (state) => {
        return state.components.timelogs.logsToRemoveQueue
      },
      reportsFilters(state) {
        return {
          info: state.components.reports.filtersInfo,
          filters: state.components.reports.filters
        }
      },
      expenseStatusOptions(state) {
        return state.components.expenses.statusOptions
      },
      selectedExpenseStatus(state) {
        return state.components.expenses.selectedStatus
      },
      selectedExpenseDateRange(state) {
        return state.components.expenses.selectedDateRange
      },
      expenseSearchQuery(state) {
        return state.components.expenses.searchQuery
      },
      expensePager(state) {
        return state.components.expenses.pager
      },
      userExpensePager(state) {
        return state.components.userexpenses.pager
      },
      formData(state) {
        return state.components.formData
      },
      formLayouts(state) {
        return state.components.formLayouts
      },
      activityItems(state) {
        return state.components.activityItems
      },
      bankHolidays(state) {
        return state.caches.bankHolidays
      },
      approvedHolidays(state) {
        return state.components.leaves.approvedList
      },
      leavesFilters(state) {
        return state.components.leaves.filters
      },
      assetBoardPager(state) {
        const { page, perPage } = state.components.assetBoard
        return { page, perPage }
      },
      assetBoardFilters(state) {
        return state.components.assetBoard.filters
      },
      assetBoardSort(state) {
        return state.components.assetBoard.sort
      },
      assetBoardClientView(state) {
        return state.components.assetBoard.clientView
      },
      assetBoardTab(state) {
        return state.components.assetBoard.tab
      },
      assetBoardSelectedAssets(state) {
        return state.components.assetBoard.selectedAssets
      },
      assetBoardRejectedAssets(state) {
        return state.components.assetBoard.rejectedAssets
      },
      projectsTrackerPager(state) {
        const { page, perPage, total } = state.components.projectsTracker
        return { page, perPage, total }
      },
      projectsTrackerCache(state) {
        const { cacheControl } = state.components.projectsTracker
        return cacheControl
      },
      timelogMonthInfo(state) {
        return state.components.timelogs.monthInfo
      },
      timelogCalendarFields(state) {
        return state.components.timelogs.calendarFields
      },
      userStatsSort(state) {
        return state.components.userStats.sort
      }
    },
    mutations: {
      // `type` is one of 'session' | 'network' | 'server'. A new banner
      // replaces the current one — only ever one at a time, so the bar cannot
      // grow and push the tabs off screen.
      setStatusBanner(state, type = null) {
        state.statusBanner = type
      },
      clearStatusBanner(state) {
        state.statusBanner = null
      },
      setUser(state, user = {}) {
        state.user = {
          authenticated: true,
          hasAdminPrivileges: ['superadmin', 'admin'].includes(user?.role) ?? false,
          hasSuperAdminPrivileges: user?.role === 'superadmin' ?? false,
          hasManagerPrivileges: user?.role === 'manager' ?? false,
          hasPartnerPrivileges: user?.role === 'partner' ?? false,
          details: user
        }
      },
      rmUser(state) {
        state.user = {
          authenticated: false,
          hasAdminPrivileges: false,
          hasSuperAdminPrivileges: false,
          hasManagerPrivileges: false,
          hasPartnerPrivileges: false,
          details: {}
        }
      },
      setUserAuthenticationStatus(state, status) {
        state.user.authenticated = status
      },
      setStaffingUserRoles(state, userRoles) {
        state.components.staffing.userRoles = userRoles
      },
      setStaffingSelectedRoles(state, selectedRoles) {
        state.components.staffing.selectedRoles = selectedRoles
      },
      setStaffingUsers(state, users) {
        state.components.staffing.users = users
      },
      setStaffingLogsUsers(state, users) {
        state.components.staffinglogs.users = users
      },
      setStaffingActiveProjects(state, projects) {
        state.components.staffing.activeProjects = projects
      },
      setTimelogsCalendarWeeksStartDate(
        state,
        startDate = startOfWeek(new Date(), { weekStartsOn: 1 })
      ) {
        state.components.timelogs.calendarWeeksStartDate = startDate
      },
      setStaffingCalendarWeeksStartDate(
        state,
        startDate = startOfWeek(new Date(), { weekStartsOn: 1 })
      ) {
        state.components.staffing.calendarWeeksStartDate = startDate
      },
      setStaffingLogsCalendarWeeksStartDate(
        state,
        startDate = startOfWeek(new Date(), { weekStartsOn: 1 })
      ) {
        state.components.staffinglogs.calendarWeeksStartDate = startDate
      },
      setStaffingProjectToDrag(state, project) {
        state.components.staffing.projectToDrag = project
      },
      removeAssigningQueue(state, { bid, puid }) {
        const currentQueue = this.getters.assigningQueue
        const assignmentIndex = currentQueue.findIndex(
          (tl) => tl.bid === bid && tl.puid === puid
        )
        if (assignmentIndex != -1) {
          state.components.staffing.assigningQueue.splice(assignmentIndex, 1)
          state.components.staffing.lastQueueUpdate.push(Date.now())
        }
      },
      updateAssigningQueue(state, { _blockId, projectUid = null }) {
        if (!_blockId) {
          state.components.staffing.assigningQueue = []
          return
        }
        const existedIndex = state.components.staffing.assigningQueue.findIndex(itm => itm.bid === _blockId)
        if (existedIndex > -1) {
          state.components.staffing.assigningQueue[existedIndex] = { bid: _blockId, puid: projectUid }
        } else {
          state.components.staffing.assigningQueue.push({ bid: _blockId, puid: projectUid })
        }
        state.components.staffing.lastQueueUpdate.push(Date.now())
      },
      setTimelogCalendarWeeksStartDate(
        state,
        startDate = startOfWeek(new Date(), { weekStartsOn: 1 })
      ) {
        state.components.timelogs.calendarWeeksStartDate = startDate
      },
      timelogToQueue(state, { project, date, hours }) {
        const currentQueue = this.getters.timelogsInQueue
        const timelogIndex = currentQueue.findIndex(
          (tl) => tl.project === project && tl.date === date
        )
        if (timelogIndex != -1) {
          state.components.timelogs.logsInQueue[timelogIndex].hours = hours
        } else {
          state.components.timelogs.logsInQueue.push({ project, date, hours })
        }
      },
      removeTimelogFromQueue(state, { project, date }) {
        const currentQueue = this.getters.timelogsInQueue
        const timelogIndex = currentQueue.findIndex(
          (tl) => tl.project === project && tl.date === date
        )
        if (timelogIndex != -1) {
          state.components.timelogs.logsInQueue.splice(timelogIndex, 1)
        }
      },
      clearTimelogQueue(state) {
        state.components.timelogs.logsInQueue = []
      },
      clearDeleteTimelogQueue(state) {
        state.components.timelogs.logsToRemoveQueue = []
      },
      timelogToRemoveQueue(state, { project, date }) {
        const currentQueue = this.getters.timelogsToRemoveQueue
        const timelogIndex = currentQueue.findIndex(
          (tl) => tl.project === project && tl.date === date
        )
        if (timelogIndex == -1) {
          state.components.timelogs.logsToRemoveQueue.push({
            project,
            date
          })
        }
      },
      deleteTimelogFromRemoveQueue(state, { project }) {
        const currentQueue = this.getters.timelogsToRemoveQueue
        const timelogIndex = currentQueue.findIndex(
          (tl) => tl.project === project
        )
        if (timelogIndex != -1) {
          state.components.timelogs.logsToRemoveQueue = currentQueue.filter(
            (tl) => tl.project !== project
          )
        }
      },
      setReportsFilters(state, { filter, data }) {
        switch (filter) {
          case 'project':
            // state.components.reports.filters.project = data.uid
            // state.components.reports.filtersInfo.projectTitle = data.title
            if (data.uid) {
              state.components.reports.filters.projects.push(data.uid)
              state.components.reports.filtersInfo.projectTitles.push(data.title)
            }
            break;
          case 'user':
            state.components.reports.filters.user = data.uid
            state.components.reports.filtersInfo.userFullName = data.fullName
            break;
          case 'dates':
            state.components.reports.filters.dates = data
            break;
        }
      },
      deleteReportsFilters(state, { filter, data }) {
        switch (filter) {
          case 'project':
            state.components.reports.filters.projects = state.components.reports.filters.projects.filter(p => p !== data.uid)
            state.components.reports.filtersInfo.projectTitles = state.components.reports.filtersInfo.projectTitles.filter(p => p !== data.title)
            break;
          case 'user':
            state.components.reports.filters.user = null
            state.components.reports.filtersInfo.userFullName = ''
            break;
          case 'dates':
            state.components.reports.filters.dates = []
            break;
        }
      },
      setExpenseStatusOptions(state, options) {
        state.components.expenses.statusOptions = options
        options.forEach(({ value }) => {
          state.components.expenses.selectedStatus.push(value)
        })
      },
      setSelectedExpenseStatus(state, statuses) {
        state.components.expenses.selectedStatus = statuses
      },
      setSelectedExpenseDateRange(state, dateRange) {
        state.components.expenses.selectedDateRange = {
          ...state.components.expenses.selectedDateRange,
          ...dateRange
        }
      },
      setExpenseSearchQuery(state, q) {
        q = q.trim()
        state.components.expenses.searchQuery = q.length > 1 ? q : ''
      },
      cacheBankHolidays(state, bankHolidays) {
        state.caches.bankHolidays.data = bankHolidays
        state.caches.bankHolidays.ts = new Date()
      },
      modifyFormData(state, { key, value }) {
        state.components.formData[key] = value
      },
      resetFormData(state) {
        state.components.formData = {
          type: 'def',
          project: 'def',
          dates: null,
          expenseProjType: 'prj',
          holidayType: null,
          holidayHalfDayMeridiem: 'AM',
          category: 'def',
          currency: 'gbp',
          amount: 0,
          holiday: 'full',
          duration: '1',
          approvalmanager: 'def',
          vat: {
            value: '0',
            amount: 0
          },
          totalDays: 1,
          notes: '',
          file: null,
          totalBankHolidaysInDates: 0
        }
      },
      setFormDataAmount(state, value) {
        state.components.formData.amount = parseFloat(value).toFixed(2)
        this.commit('setFormDataVat', state.components.formData.vat)
      },
      setFormDataVat(state, { amount, value }) {
        state.components.formData.vat.value = value === 'other' ? value : parseInt(value)
        if (state.components.formData.vat.value === 'other') {
          state.components.formData.vat.amount = parseFloat(amount).toFixed(2)
        } else {
          state.components.formData.vat.amount = parseFloat((parseFloat(state.components.formData.amount) * state.components.formData.vat.value) / 100).toFixed(2)
        }
      },
      setFormLayouts(state, { layout, show }) {
        state.components.formLayouts[layout] = show
      },
      resetFormLayouts(state) {
        for (let key in state.formLayouts) {
          state.components.formLayouts[key] = false
        }
      },
      addActivityEntry(state, { list, item }) {
        state.components.activityItems[list].push(item)
      },
      modifyActivityEnteries(state, { list, value }) {
        state.components.activityItems[list] = value
      },
      clearActivityEnteries(state) {
        for (let key in state.components.activityItems) {
          state.components.activityItems[key] = []
        }
      },
      setApprovedHolidays(state, holidays) {
        state.components.leaves.approvedList = []
        holidays = holidays.filter(h => {
          return h.totalDays > 0.5 && h.status === 'approved'
        }).forEach(h => {
          if (state.components.leaves.approvedList.includes(h.startDate)) {
            return
          }
          for (let i = 0; i < h.totalDays; i++) {
            state.components.leaves.approvedList.push(format(addDays(new Date(h.startDate), i), 'yyyy-MM-dd'))
          }
        })
      },
      setLeavesFilters(state, { filter, data }) {
        state.components.leaves.filters[filter] = data
      },
      setAssetBoardPager(state, { page, perPage }) {
        if (page) {
          state.components.assetBoard.page = page
        }
        if (perPage) {
          state.components.assetBoard.perPage = perPage
        }
      },
      setAssetBoardFilters(state, filters) {
        state.components.assetBoard.filters = filters
      },
      setAssetBoardSort(state, { sortBy, sortDesc }) {
        state.components.assetBoard.sort = [sortBy, sortDesc ? 'desc' : 'asc']
      },
      setAssetBoardClientView(state, clientView) {
        state.components.assetBoard.clientView = clientView
      },
      setAssetBoardTab(state, tab) {
        state.components.assetBoard.tab = tab
      },
      toggleSelectedAsset(state, assetUid) {
        const index = state.components.assetBoard.selectedAssets.indexOf(assetUid)
        if (index === -1) {
          state.components.assetBoard.selectedAssets.push(assetUid)
        } else {
          state.components.assetBoard.selectedAssets.splice(index, 1)
        }
      },
      toggleRejectedAsset(state, assetUid) {
        const index = state.components.assetBoard.rejectedAssets.indexOf(assetUid)
        if (index === -1) {
          state.components.assetBoard.rejectedAssets.push(assetUid)
        } else {
          state.components.assetBoard.rejectedAssets.splice(index, 1)
        }
      },
      resetReservedAssets(state) {
        state.components.assetBoard.selectedAssets = []
        state.components.assetBoard.rejectedAssets = []
      },
      resetAssetBoardFilters(state, filters = {}, force = false) {
        const keysToKeep = []
        if (!force) {
          for (const key in filters) {
            if (filters[key].length) {
              keysToKeep.push(key)
            }
          }
        }
        state.components.assetBoard.filters = {}
        if (!force && keysToKeep.length) {
          keysToKeep.forEach(key => {
            state.components.assetBoard.filters[key] = filters[key]
          })
        }
      },
      updProjectsTrackerPager(state, { page, perPage, total }) {
        if (page) {
          state.components.projectsTracker.page = page
        }
        if (perPage) {
          state.components.projectsTracker.perPage = perPage
        }
        if (total) {
          state.components.projectsTracker.total = total
        }
      },
      setProjectsTrackerCache(state, val) {
        state.components.projectsTracker.cacheControl = ['cache', 'no-cache'].includes(val) ? val : 'cache'
      },
      setHolidaySelectedYear(state, year) {
        state.components.leaves.newHoliday.selectedYear = year
      },
      setTimelogMonthInfo(state, monthInfo) {
        state.components.timelogs.monthInfo = monthInfo
      },
      setTimelogCalendarFields(state, fields) {
        state.components.timelogs.calendarFields = fields
      },
      updateNotifications(state, notifications) {
        state.userNotifications = notifications
      },
      setExpensePager(state, data) {
        state.components.expenses.pager = {
          ...state.components.expenses.pager,
          ...data
        }
      },
      setUserExpensePager(state, data) {
        state.components.userexpenses.pager = {
          ...state.components.userexpenses.pager,
          ...data
        }
      },
      setUserStatsSort(state, { field, order }) {
        state.components.userStats.sort = [field, order]
      },
    }
  })
}
